import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import type { Competitor, DiceRoll, IdeaDTO } from "../lib/api";
import { Icons } from "./Icons";
import { t } from "../lib/i18n";
import { pickVerbs } from "../lib/verbs";
import { shareIdea } from "../lib/telegram";
import { ThinkingAnimation } from "./ThinkingAnimation";
import { spriteHrefFor } from "../lib/logos";

interface IdeaModalProps {
  idea: IdeaDTO | null;
  /** Partial idea fields received from the stream before `idea` arrives. */
  partial?: Partial<IdeaDTO>;
  /** Server-rolled dice for this spin. We DON'T render them, they stay an
   *  internal randomisation knob, but we keep the prop so the type signature
   *  matches what SlotsTab passes through. */
  dice?: DiceRoll | null;
  /** Exa-fetched real-world competitors, may arrive a beat after the idea. */
  competitors?: Competitor[];
  /** True until the stream signals competitor lookup is finished. */
  competitorsLoading?: boolean;
  open: boolean;
  thinkingModel?: string;
  onClose: () => void;
  onToggleSave?: () => void;
  /** Called when user thumbs-downs the idea, SlotsTab persists it locally
   *  and feeds it to the worker on the next spin. */
  onReject?: () => void;
  saving?: boolean;
  jackpot?: boolean;
}

/**
 * Modal lives in two visual modes:
 *   - "building"  → idea is null but stream/partial has at least a title.
 *                   Compact ~52vh sheet. Reels still visible above.
 *   - "revealed"  → full idea has arrived. Expands to 92vh and shows
 *                   the full layout incl. actions.
 *
 * The transition is a CSS height interpolation so the sheet visibly
 * "grows up" the screen the moment streaming finishes.
 */
export function IdeaModal(props: IdeaModalProps) {
  const [copied, setCopied] = createSignal(false);
  const [shared, setShared] = createSignal(false);
  const [blurOn, setBlurOn] = createSignal(false);
  /** Live drag offset (px) while the user pulls the sheet down to dismiss. */
  const [dragOffset, setDragOffset] = createSignal(0);
  let sheetRef: HTMLDivElement | undefined;
  let closeBtnRef: HTMLButtonElement | undefined;

  // Activate the expensive backdrop-filter ONLY after slideUp completes.
  createEffect(() => {
    if (props.open) {
      setBlurOn(false);
      const tm = setTimeout(() => setBlurOn(true), 380);
      onCleanup(() => clearTimeout(tm));
    } else {
      setBlurOn(false);
      setCopied(false);
      setShared(false);
      setDragOffset(0);
    }
  });

  // Auto-reset `shared` after the same 1.4 s window as `copied` so a second
  // share within the same modal can be re-confirmed visually.
  createEffect(() => {
    if (!shared()) return;
    const tm = setTimeout(() => setShared(false), 1400);
    onCleanup(() => clearTimeout(tm));
  });

  // The modal covers the whole phone-card (including the tab bar) so we
  // don't need to lock the underlying scroll. We still wire Escape → close
  // and auto-focus the close button for keyboard users.
  createEffect(() => {
    if (typeof document === "undefined") return;
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        props.onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
    // queueMicrotask lands the focus after the slide-up's first paint but
    // without scheduling a 50 ms setTimeout (and the cleanup churn that
    // came with it on every open).
    queueMicrotask(() => closeBtnRef?.focus());
  });

  const fullyRevealed = () => !!props.idea;
  // The sheet covers the whole phone-card on desktop and the whole viewport
  // on mobile (because the card === the viewport there). 100% removes the
  // earlier broken 92vh / 52vh values which leaked the page beneath on
  // desktop. The two height modes now show via inner content density.
  const sheetHeight = () => "100%";

  const ideaNum = createMemo(() => {
    const i = props.idea;
    if (!i) return 0;
    return (Math.abs(i.title.length * 13 + i.model_label.length * 7) % 9000) + 1000;
  });

  const handleCopy = async (): Promise<void> => {
    const idea = props.idea;
    if (!idea) return;
    // Prefer the new variant-C fields; fall back to legacy fields so
    // copying an old saved idea still produces a readable block.
    const today = idea.today_they || idea.what_to_build || "";
    const instead = idea.instead_they_could || "";
    const whoPays = idea.who_pays_and_how_much || idea.who_pays || "";
    const dist = idea.distribution || idea.how_to_get_customers || "";
    const giant = idea.the_giant_in_the_room || idea.main_risk || "";
    const lines = [
      idea.title,
      idea.subtitle,
      "",
      today && `${t("idea.todayThey")}: ${today}`,
      instead && `${t("idea.insteadTheyCould")}: ${instead}`,
      whoPays && `${t("idea.whoPaysAndHowMuch")}: ${whoPays}`,
      dist && `${t("idea.distribution")}: ${dist}`,
      giant && `${t("idea.theGiantInTheRoom")}: ${giant}`,
    ].filter(Boolean) as string[];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      const t2 = setTimeout(() => setCopied(false), 1600);
      onCleanup(() => clearTimeout(t2));
    } catch {
      /* ignore */
    }
  };

  /** Look up a field, idea takes precedence, fall back to partial. */
  const v = (k: keyof IdeaDTO): any =>
    (props.idea && props.idea[k]) ?? props.partial?.[k];

  // Swipe-down-to-dismiss handlers. iOS-style: pull below 80 px and release
  // to close. Drag is no-op while the sheet content is scrolled past the
  // top, so the user can still scroll the body without accidentally
  // dismissing.
  let dragStartY: number | null = null;
  const onTouchStart = (e: TouchEvent): void => {
    if (!sheetRef) return;
    // Only start drag if the sheet is scrolled to the very top.
    if (sheetRef.scrollTop > 0) return;
    dragStartY = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: TouchEvent): void => {
    if (dragStartY == null) return;
    const y = e.touches[0]?.clientY ?? dragStartY;
    const dy = Math.max(0, y - dragStartY);
    // Avoid re-triggering the sheet transform every touchmove tick if the
    // delta did not change (iOS fires touchmove at ~60 Hz even on jitter).
    if (dy !== dragOffset()) setDragOffset(dy);
  };
  const onTouchEnd = (): void => {
    if (dragStartY == null) return;
    const dy = dragOffset();
    dragStartY = null;
    if (dy > 100) {
      props.onClose();
    }
    setDragOffset(0);
  };

  return (
    <Show when={props.open}>
      {/* absolute (was fixed): keeps the modal inside the phone-card on
          desktop and identical to fixed on mobile where the card IS the
          viewport. role/aria-modal/aria-labelledby + Esc + focus = a11y
          parity (QA P0 #6 + P1 #10/#22). */}
      <div
        class="absolute inset-0 z-[100] animate-fadeIn"
        role="dialog"
        aria-modal="true"
        aria-labelledby="idea-modal-title"
      >
        <div
          onClick={props.onClose}
          aria-hidden="true"
          class="absolute inset-0"
          style={{
            background: "rgba(20, 18, 12, 0.42)",
            "backdrop-filter": blurOn() && fullyRevealed() ? "blur(8px)" : "none",
            "-webkit-backdrop-filter": blurOn() && fullyRevealed() ? "blur(8px)" : "none",
            transition: "backdrop-filter 0.18s ease",
          }}
        />

        <div
          ref={sheetRef}
          class="modal-sheet absolute inset-x-0 bottom-0 overflow-auto animate-slideUp"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          style={{
            background: "linear-gradient(180deg, #fffefb 0%, #faf6ec 100%)",
            "border-radius": "28px 28px 0 0",
            // Top padding clears iOS notch + Telegram's own header.
            // --app-safe-top is defined in index.css and combines both
            // Telegram-reported insets with a fallback to native env()
            // when the app runs outside Telegram. Padding-bottom is 0
            // so the sticky footer sits flush; the footer itself adds
            // env(safe-area-inset-bottom).
            padding: "calc(var(--app-safe-top) + 12px) 18px 0",
            height: sheetHeight(),
            transition: dragOffset() === 0 ? "transform 0.18s ease, height 0.65s cubic-bezier(.22,1,.36,1)" : "none",
            transform: dragOffset() > 0 ? `translateY(${dragOffset()}px)` : "none",
            "box-shadow": "0 -20px 60px rgba(120, 95, 40, 0.18)",
            border: "1px solid rgba(201, 169, 97, 0.2)",
            "overscroll-behavior": "contain",
          }}
        >
          {/* Drag handle. Visual + a11y label. */}
          <button
            type="button"
            onClick={props.onClose}
            aria-label={t("common.close")}
            style={{
              display: "block",
              width: "56px",
              height: "20px",
              padding: 0,
              margin: "0 auto 8px",
              background: "transparent",
              border: 0,
              cursor: "pointer",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: "38px",
                height: "5px",
                "border-radius": "3px",
                background: "rgba(201, 169, 97, 0.45)",
                margin: "7px auto 0",
              }}
            />
          </button>

          <BuildingView
            idea={props.idea ?? null}
            v={v}
            competitors={props.competitors ?? []}
            competitorsLoading={!!props.competitorsLoading}
            thinkingModel={props.thinkingModel || ""}
            jackpot={!!props.jackpot}
            fullyRevealed={fullyRevealed()}
            ideaNum={ideaNum()}
            copied={copied()}
            shared={shared()}
            onCopy={handleCopy}
            onShare={() => setShared(true)}
            onClose={props.onClose}
            onToggleSave={props.onToggleSave}
            onReject={props.onReject}
            saving={!!props.saving}
            closeBtnRef={(el) => (closeBtnRef = el)}
          />
        </div>
      </div>
    </Show>
  );
}

/* ── BuildingView ─────────────────────────────────────────────────────── */

interface BuildingViewProps {
  idea: IdeaDTO | null;
  v: (k: keyof IdeaDTO) => any;
  competitors: Competitor[];
  competitorsLoading: boolean;
  thinkingModel: string;
  jackpot: boolean;
  fullyRevealed: boolean;
  ideaNum: number;
  copied: boolean;
  shared: boolean;
  onCopy: () => void;
  onShare: () => void;
  onClose: () => void;
  onToggleSave?: () => void;
  onReject?: () => void;
  saving: boolean;
  closeBtnRef?: (el: HTMLButtonElement) => void;
}

function BuildingView(p: BuildingViewProps) {
  return (
    <div>
      {/* Header row, close button + (when revealed) idea pill */}
      <div class="flex items-start justify-between" style={{ "margin-bottom": "10px" }}>
        <Show
          when={p.fullyRevealed}
          fallback={<ThinkingPill model={p.thinkingModel} jackpot={p.jackpot} />}
        >
          <IdeaNumPill ideaNum={p.ideaNum} jackpot={p.jackpot} />
        </Show>
        <button
          ref={p.closeBtnRef}
          onClick={p.onClose}
          aria-label={t("common.close")}
          class="flex cursor-pointer items-center justify-center rounded-full border-0"
          style={{
            // 44×44 minimum target so the close button is reachable
            // on every mobile size (QA P1 #9 / #11).
            width: "44px",
            height: "44px",
            background: "rgba(0,0,0,0.04)",
            "flex-shrink": 0,
          }}
        >
          <Icons.Close size={18} color="#666" />
        </button>
      </div>



      {/* Title, streams in when available, otherwise we rotate progress phrases
          so the user never sees a frozen "BREWING" placeholder. */}
      <div
        id="idea-modal-title"
        class="serif"
        style={{
          "font-size": p.fullyRevealed ? "34px" : "28px",
          "font-weight": 500,
          color: "#1a1a1a",
          "line-height": 1.1,
          "letter-spacing": "-0.5px",
          "margin-bottom": "6px",
          "min-height": "32px",
          transition: "font-size 0.4s ease",
        }}
      >
        <Show when={p.v("title")} fallback={<BrewingPhrase />}>
          {p.v("title")}
        </Show>
      </div>

      {/* While the idea hasn't fully arrived yet, fill the empty sheet
         with the picked LLM's logo + "<Model> is thinking...". Hidden
         the moment content streams in. */}
      <Show when={!p.fullyRevealed && p.thinkingModel}>
        <ThinkingAnimation modelLabel={p.thinkingModel} />
      </Show>

      {/* Subtitle */}
      <Show when={p.v("subtitle")}>
        <div style={{ "font-size": "13px", color: "#7a7a7a", "margin-bottom": "14px" }}>
          {p.v("subtitle")}
        </div>
      </Show>

      {/* Single model chip, v2 prompt dropped niche/context/hot_score/
          check/region/cac, so the only context badge worth showing is
          which model generated this idea. */}
      <Show when={p.v("model_label")}>
        <div class="flex gap-1.5" style={{ "margin-bottom": "14px" }}>
          <Chip label={p.v("model_label")} sub={t("idea.labelTech")} />
        </div>
      </Show>

      {/* Variant-C sections (today → instead → buyer → channels →
          incumbent). For legacy saved ideas where the new columns are
          empty, fall back to the v2 fields so the modal still has
          content. */}
      <Section
        label={t("idea.todayThey")}
        body={p.v("today_they") || p.v("what_to_build")}
      />
      <Section
        label={t("idea.insteadTheyCould")}
        body={p.v("instead_they_could")}
      />
      <Section
        label={t("idea.whoPaysAndHowMuch")}
        body={p.v("who_pays_and_how_much") || p.v("who_pays")}
      />
      <Section
        label={t("idea.distribution")}
        body={p.v("distribution") || p.v("how_to_get_customers")}
      />
      <Section
        label={t("idea.theGiantInTheRoom")}
        body={p.v("the_giant_in_the_room") || p.v("main_risk")}
      />

      {/* Live competitors from Exa.ai. Always render the section once the
          idea is fully revealed, it shows a loading placeholder until the
          competitors event arrives, then morphs into the result list. */}
      <Show when={p.fullyRevealed}>
        <CompetitorsSection
          competitors={p.competitors}
          loading={p.competitorsLoading}
        />
      </Show>

      {/* Footer: loading dots until fully revealed, then action buttons */}
      <Show
        when={p.fullyRevealed && p.idea}
        fallback={<LoadingDots />}
      >
        {/* Sticky footer. The action bar lives in a separate position:sticky
            strip so Save / Reject are always visible without scrolling on
            1024×700 desktops (QA P0 #2). The modal-sheet has padding-bottom:0
            so this can sit flush at the container bottom. */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            "margin-top": "24px",
            "margin-left": "-18px",
            "margin-right": "-18px",
            padding: "12px 18px calc(var(--app-safe-bottom) + 12px)",
            background:
              "linear-gradient(180deg, rgba(255,254,251,0) 0%, rgba(255,254,251,0.96) 18%, #fffefb 35%)",
            "backdrop-filter": "blur(4px)",
          }}
        >
          <div class="flex gap-2">
            <button onClick={p.onCopy} style={iconBtn} aria-label={t("common.copy")} aria-live="polite">
              {p.copied ? <Icons.Check size={18} color="#1a1a1a" /> : <Icons.Copy size={18} />}
            </button>
            <button
              onClick={() => {
                shareIdea(p.idea!.title, p.idea!.subtitle);
                p.onShare();
              }}
              style={iconBtn}
              aria-label={t("common.share")}
            >
              {p.shared ? <Icons.Check size={18} color="#1a1a1a" /> : <Icons.Share size={18} />}
            </button>
            <button
              onClick={p.onToggleSave}
              disabled={p.saving}
              style={{
                ...primaryBtn,
                background: p.idea?.saved
                  ? "linear-gradient(180deg, #fff 0%, #faf6ec 100%)"
                  : "linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)",
                color: p.idea?.saved ? "#a89358" : "#fff",
                border: p.idea?.saved ? "1px solid rgba(201, 169, 97, 0.45)" : "none",
                opacity: p.saving ? 0.6 : 1,
                cursor: p.saving ? "default" : "pointer",
              }}
            >
              {p.idea?.saved ? t("idea.savedBadge") : t("idea.saveIdea")}
            </button>
          </div>
          {/* Reject. Persists to localStorage and feeds back into the worker
              on the next spin so the LLM stops generating similar shapes. */}
          <Show when={p.onReject}>
            <button
              onClick={p.onReject}
              class="flex items-center justify-center gap-1.5"
              style={{
                width: "100%",
                "margin-top": "10px",
                "min-height": "44px",
                padding: "10px 12px",
                background: "transparent",
                border: "1px dashed rgba(195, 51, 51, 0.4)",
                "border-radius": "14px",
                "font-family": "inherit",
                "font-size": "13px",
                "font-weight": 600,
                color: "#a32a2a",
                cursor: "pointer",
                "letter-spacing": "0.3px",
              }}
            >
              👎 {t("idea.reject")}
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}

/* ── Subcomponents ────────────────────────────────────────────────────── */

/**
 * Slim CSS-animated progress bar pinned to the very top of the sheet. Even
 * when the LLM is silent (non-streaming models), the user sees physical
 * forward motion and doesn't think the app froze. Curve rushes to ~75%
 * in the first 4s, then crawls toward 95% over the next 12s.
 */
function ProgressBar() {
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: "rgba(201,169,97,0.10)",
        "border-radius": "3px 3px 0 0",
        overflow: "hidden",
        "pointer-events": "none",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          height: "100%",
          width: "0%",
          background: "linear-gradient(90deg, #e8d4a2, #c9a961, #b8923f)",
          "box-shadow": "0 0 6px rgba(201, 169, 97, 0.55)",
          animation: "sheetProgress 16s cubic-bezier(.05,.7,.2,.95) forwards",
        }}
      />
    </div>
  );
}

function ThinkingPill(p: { model: string; jackpot: boolean }) {
  return (
    <div
      class="inline-flex items-center gap-1.5"
      style={{
        padding: "5px 11px",
        background: p.jackpot
          ? "linear-gradient(135deg, rgba(232,212,162,0.35), rgba(184,146,63,0.25))"
          : "rgba(201, 169, 97, 0.12)",
        border: "1px solid rgba(201, 169, 97, 0.4)",
        "border-radius": "100px",
        "font-size": "10.5px",
        "font-weight": 700,
        color: "#8e6f2a",
        "text-transform": "uppercase",
        "letter-spacing": "1px",
      }}
    >
      <Icons.Sparkle size={10} />
      {t("idea.thinking", { model: p.model })}
    </div>
  );
}

function IdeaNumPill(p: { ideaNum: number; jackpot: boolean }) {
  return (
    <div
      class="inline-flex items-center gap-1.5"
      style={{
        padding: "5px 11px",
        background: p.jackpot
          ? "linear-gradient(135deg, rgba(201, 169, 97, 0.4), rgba(201, 169, 97, 0.18))"
          : "linear-gradient(135deg, rgba(201, 169, 97, 0.18), rgba(201, 169, 97, 0.08))",
        border: "1px solid rgba(201, 169, 97, 0.4)",
        "border-radius": "100px",
        "font-size": "10px",
        "font-weight": 700,
        color: p.jackpot ? "#8e6f2a" : "#a89358",
        "text-transform": "uppercase",
        "letter-spacing": "1px",
      }}
    >
      <Icons.Sparkle size={10} />
      {p.jackpot
        ? `${t("slots.jackpot")} · ${t("idea.ideaNum", { n: p.ideaNum })}`
        : t("idea.ideaNum", { n: p.ideaNum })}
    </div>
  );
}

/**
 * Rotating "thinking" phrase shown while we wait for the first content token.
 * 187 playful gerunds from Claude Code's spinnerVerbs.txt, we pick a random
 * 8 per spin and rotate every 1.2s. Both EN and RU pools are kept in sync.
 */
function BrewingPhrase() {
  const verbs = pickVerbs(8);
  const [idx, setIdx] = createSignal(0);
  const handle = setInterval(() => setIdx((n) => (n + 1) % verbs.length), 1200);
  onCleanup(() => clearInterval(handle));
  return (
    <span
      class="text-shimmer"
      style={{
        display: "inline-block",
        transition: "opacity 0.3s ease",
      }}
    >
      {verbs[idx()]}…
    </span>
  );
}

function LoadingDots() {
  return (
    <div
      class="flex items-center justify-center gap-2"
      style={{ padding: "18px 0 8px", "margin-top": "8px" }}
    >
      <For each={[0, 1, 2]}>
        {(i) => (
          <div
            style={{
              width: "9px",
              height: "9px",
              "border-radius": "5px",
              background: "linear-gradient(135deg, #d4b56e, #b8923f)",
              animation: `dotPulse 1.1s ease-in-out ${i * 0.14}s infinite`,
            }}
          />
        )}
      </For>
    </div>
  );
}

function Chip(p: { label: string; sub: string }) {
  // Look up the same-doc sprite href the slot reel uses. The sprite is
  // mounted once at app root by <SpriteInline />; we just <use> by id.
  const href = () => spriteHrefFor(p.label);
  return (
    <div
      class="flex min-w-0 flex-1 items-center gap-2.5"
      style={{
        padding: "8px 12px",
        background: "#fff",
        border: "1px solid rgba(201, 169, 97, 0.4)",
        "border-radius": "12px",
      }}
    >
      <Show when={href()}>
        {(hr) => (
          <span
            class="flex items-center justify-center shrink-0"
            style={{
              width: "28px",
              height: "28px",
              "border-radius": "8px",
              background: "linear-gradient(135deg, #fff 0%, #faf2dd 100%)",
              border: "1px solid rgba(201, 169, 97, 0.2)",
            }}
          >
            <svg
              width="18"
              height="18"
              style={{ display: "block" }}
              aria-hidden="true"
            >
              <use href={hr()} />
            </svg>
          </span>
        )}
      </Show>
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <div
          style={{
            "font-size": "10.5px",
            color: "#8e6f2a",
            "text-transform": "uppercase",
            "letter-spacing": "0.8px",
            "font-weight": 700,
          }}
        >
          {p.sub}
        </div>
        <div
          class="serif"
          style={{
            "font-size": "15px",
            "font-weight": 600,
            color: "#1a1a1a",
            "word-break": "break-word",
            "line-height": 1.25,
          }}
        >
          {p.label}
        </div>
      </div>
    </div>
  );
}

function CompetitorsSection(p: { competitors: Competitor[]; loading: boolean }) {
  const empty = () => !p.loading && p.competitors.length === 0;
  return (
    <div
      class="animate-fadeIn"
      style={{ "margin-bottom": "14px", "margin-top": "4px" }}
    >
      <div
        style={{
          // 10.5 px / #8e6f2a clears AA for small caps text (QA P1 #12).
          "font-size": "10.5px",
          "font-weight": 700,
          color: "#8e6f2a",
          "text-transform": "uppercase",
          "letter-spacing": "1.2px",
          "margin-bottom": "8px",
          display: "flex",
          "align-items": "center",
          gap: "6px",
        }}
      >
        <span style={{ "font-size": "12px" }} aria-hidden="true">🔎</span>
        {t("idea.competitors")}
        <Show when={p.loading}>
          <span
            style={{
              "margin-left": "6px",
              "font-size": "10px",
              color: "#b8923f",
              opacity: 0.85,
              "text-transform": "lowercase",
              "letter-spacing": "0.4px",
            }}
          >
            · {t("idea.competitorsSearching")}
          </span>
        </Show>
      </div>
      <Show when={p.loading && p.competitors.length === 0}>
        <div class="flex flex-col gap-1.5">
          <For each={[0, 1, 2]}>
            {(i) => (
              <div
                style={{
                  height: "54px",
                  background:
                    "linear-gradient(90deg, rgba(201,169,97,0.06), rgba(201,169,97,0.14), rgba(201,169,97,0.06))",
                  "background-size": "200% 100%",
                  "border-radius": "12px",
                  border: "1px solid rgba(201, 169, 97, 0.18)",
                  animation: `shimmer 1.6s linear infinite ${i * 0.15}s`,
                }}
              />
            )}
          </For>
        </div>
      </Show>
      <Show when={empty()}>
        <div
          style={{
            padding: "12px",
            background: "#fff",
            border: "1px dashed rgba(201, 169, 97, 0.35)",
            "border-radius": "12px",
            "font-size": "12.5px",
            color: "#5a5a5a",
            "line-height": 1.45,
          }}
        >
          {t("idea.competitorsEmpty")}
        </div>
      </Show>
      <div class="flex flex-col gap-1.5">
        <For each={p.competitors}>
          {(c) => (
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Telegram in-app browser is preferable when available.
                const tg = (window as any).Telegram?.WebApp;
                if (tg?.openLink) {
                  e.preventDefault();
                  tg.openLink(c.url);
                }
              }}
              style={{
                display: "block",
                padding: "9px 12px",
                background: "#fff",
                border: "1px solid rgba(201, 169, 97, 0.22)",
                "border-radius": "12px",
                "text-decoration": "none",
                color: "inherit",
                "min-width": 0,
              }}
            >
              <div
                style={{
                  "font-size": "13px",
                  "font-weight": 600,
                  color: "#1a1a1a",
                  "line-height": 1.25,
                  overflow: "hidden",
                  "text-overflow": "ellipsis",
                  "white-space": "nowrap",
                }}
              >
                {c.title}
              </div>
              <div
                style={{
                  "font-size": "11px",
                  color: "#8e6f2a",
                  "margin-top": "2px",
                  overflow: "hidden",
                  "text-overflow": "ellipsis",
                  "white-space": "nowrap",
                }}
              >
                {hostOf(c.url)}
              </div>
              <Show when={c.blurb}>
                <div
                  style={{
                    "font-size": "12px",
                    color: "#5a5a5a",
                    "margin-top": "4px",
                    "line-height": 1.4,
                    display: "-webkit-box",
                    "-webkit-line-clamp": 2,
                    "-webkit-box-orient": "vertical",
                    overflow: "hidden",
                  }}
                >
                  {c.blurb}
                </div>
              </Show>
            </a>
          )}
        </For>
      </div>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

interface ParsedStep {
  num: string | null;
  text: string;
}

function parseSteps(body: string): ParsedStep[] {
  const raw = (body || "").trim();
  if (!raw) return [];
  // Explicit newlines first.
  let chunks: string[];
  if (raw.includes("\n")) {
    chunks = raw.split("\n").map((s) => s.trim()).filter(Boolean);
  } else {
    // Inline "1) ", "2) " markers, break BEFORE the next number so the
    // marker stays attached to its text.
    const inlineHits = raw.match(/(?:^|\s)(\d+\))\s/g);
    if (inlineHits && inlineHits.length >= 2) {
      chunks = raw.split(/(?=\s\d+\)\s)/).map((s) => s.trim()).filter(Boolean);
    } else {
      chunks = [raw];
    }
  }
  // Pull leading "1)" / "1." out of each chunk so we can render a real
  // hanging-indent list with a styled number column.
  return chunks.map((chunk) => {
    const m = chunk.match(/^(\d+)[).]\s+(.*)$/s);
    return m ? { num: m[1], text: m[2] } : { num: null, text: chunk };
  });
}

function Section(p: { label: string; body: string }) {
  const steps = () => parseSteps(p.body);
  // True when at least half the chunks have an explicit "1)" marker,
  // those render as a numbered list with a hanging indent. Otherwise
  // it's prose and we render a single relaxed paragraph.
  const isList = () => {
    const ss = steps();
    if (ss.length < 2) return false;
    return ss.filter((s) => s.num !== null).length >= ss.length / 2;
  };
  return (
    <Show when={p.body}>
      <div style={{ "margin-bottom": "18px" }}>
        <div
          style={{
            "font-size": "11px",
            "font-weight": 700,
            color: "#8a6f1f",
            "text-transform": "uppercase",
            "letter-spacing": "1.4px",
            "margin-bottom": "10px",
          }}
        >
          {p.label}
        </div>
        <Show
          when={isList()}
          fallback={
            <div
              style={{
                "font-size": "15px",
                "line-height": 1.6,
                color: "#1a1a1a",
                "font-feature-settings": "'kern' 1, 'liga' 1",
              }}
            >
              {p.body}
            </div>
          }
        >
          <ol
            style={{
              display: "flex",
              "flex-direction": "column",
              gap: "10px",
              margin: 0,
              padding: 0,
              "list-style": "none",
            }}
          >
            <For each={steps()}>
              {(step) => (
                <li
                  style={{
                    display: "grid",
                    "grid-template-columns": "22px 1fr",
                    "column-gap": "10px",
                    "align-items": "baseline",
                  }}
                >
                  <span
                    class="serif"
                    style={{
                      "font-size": "15px",
                      "font-weight": 600,
                      color: "#b8923f",
                      "line-height": 1.5,
                      "text-align": "right",
                    }}
                  >
                    {step.num ?? "•"}
                  </span>
                  <span
                    style={{
                      "font-size": "15px",
                      "line-height": 1.55,
                      color: "#1a1a1a",
                    }}
                  >
                    {step.text}
                  </span>
                </li>
              )}
            </For>
          </ol>
        </Show>
      </div>
    </Show>
  );
}

const iconBtn = {
  width: "48px",
  height: "48px",
  "border-radius": "24px",
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.08)",
  display: "flex",
  "align-items": "center",
  "justify-content": "center",
  cursor: "pointer",
  "flex-shrink": 0,
} as const;

const primaryBtn = {
  flex: 1,
  height: "48px",
  "border-radius": "24px",
  "font-family": "inherit",
  "font-size": "15px",
  "font-weight": 600,
  cursor: "pointer",
  "letter-spacing": "0.3px",
} as const;
